<template>
  <div relative>
    <div relative min-h-lg>
      <MapContour
        v-if="userLocation || mapLoaded"
        :options="options"
        @load="loadMap"
        :sources="sources"
        :layers="layers"
        :marker="true"
        :show-labels="false"
        @markerMove="$emit('markerMove', $event)"
        class="bg-stone-100 bg-opacity-50"
      />
      <div v-else bg-stone-100 bg-opacity-50 absolute h-full w-full></div>
      <transition enter-from-class="translate-y-10 scale-80 opacity-0" enter-active-class="transition duration-300">
        <div
          class="my-2 absolute bottom-0 sm:bottom-12 left-0 m-2 p-2 px-4 rounded-3 bg-orange-50 bg-opacity-40 backdrop-blur-sm shadow shadow-neutral-400"
          v-if="mapLoaded && snowfall"
        >
          <div font-mono text-stone-700 font-600 lh-snug text-lg>Snow Depth</div>
          <p text-5xl sm:text-6xl font-bold>{{ snowfall }}</p>
        </div>
      </transition>
    </div>
    <!-- <p color-stone-500 sm:hidden v-if="selectedMeta?.endTime">
      <strong font-semibold>Updated:</strong>
      {{ selectedMeta.startTime }} - {{ selectedMeta.endTime }}.<br />
      <strong font-semibold v-if="selectedMeta?.maxSnowfallInInches">Max snowfall:</strong>
      {{ selectedMeta.maxSnowfallInInches }} inches.
    </p> -->
    <Legend class="my-4 sm:(absolute bottom-0 right-0 m-2)" :options="legend" />
  </div>
  <!-- <p class="color-stone-500 hidden sm:block">
    <template v-if="selectedMeta.data.value?.endTime">
      <strong font-semibold>Updated:</strong>
      {{ selectedMeta.data.value.startTime }} - {{ selectedMeta.data.value.endTime }}.
    </template>
    <template v-if="selectedMeta.data.value?.maxSnowfallInInches">
      <strong font-semibold>Max snowfall:</strong>
      {{ selectedMeta.data.value.maxSnowfallInInches }} inches.
    </template>
  </p> -->
</template>
<script setup>
import { point } from "@turf/turf";
import Legend from "../Legend.vue";
import { useDateFormat, useNow } from "@vueuse/core";

const emit = defineEmits(["markerMove"]);
const props = defineProps({
  location: {
    type: Object,
  },
  options: {
    type: Object,
    default: {},
  },
});

let map;

const mapLoaded = ref(false);
const snowfall = ref();

const userLocation = computed(() => {
  if (isNumber(props.location?.latitude) && isNumber(props.location?.longitude)) {
    return point([props.location.longitude, props.location.latitude], {
      type: "userLocation",
    });
  }
  return null;
});

const cacheTimestamp = useDateFormat(useNow(), "YYYY-MM-DD");

function formatDate(date) {
  const day =
    date.getDate() == new Date().getDate()
      ? "Today"
      : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return day + ", " + date.toLocaleTimeString(undefined, { hour: "numeric", minute: "numeric" });
}

function useSnowfallMetaFetch(type) {
  return useLazyFetch(`latest_${type}.json`, {
    baseURL: "https://tiles.whatismyelevation.com/snowfall",
    query: {
      _: cacheTimestamp,
    },
    transform: (data) => {
      let startTime = null;
      if (data?.startTime) {
        const startDate = new Date(data?.startTime * 1000);
        startTime = formatDate(startDate);
      }
      let endTime = null;
      if (data?.endTime) {
        const endDate = new Date(data?.endTime * 1000);
        endTime =
          endDate.getDate() !== startDate.getDate()
            ? formatDate(endDate)
            : endDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "numeric" });
      }

      return {
        maxSnowfallInInches: round(data?.maxSnowfallInMeters), // data is actually in inches, not meters
        startTime,
        endTime,
      };
    },
  });
}

const selectedMeta = await useSnowfallMetaFetch("season");

const legend = computed(() => {
  const maxSnowfall = selectedMeta.data.value?.maxSnowfallInInches;
  if (maxSnowfall > 0) {
    // get the inches number from the name string and only show the legend items that are less than the max snowfall
    return SNOWFALL_SEASONAL_MAP_LEGEND.filter((item) => {
      // the item might be in or ft
      const number = parseInt(item.name.match(/\d+/)[0]);
      const inches = item.name.includes("in") ? number : number * 12;
      return inches <= maxSnowfall;
    });
  }
  return SNOWFALL_SEASONAL_MAP_LEGEND;
});

const sources = computed(() => {
  return [
    {
      id: "snow_depth",
      type: "vector",
      url: `pmtiles://https://tiles.whatismyelevation.com/snowfall/latest_snow_depth.pmtiles?v=${cacheTimestamp.value}`,
    },
  ];
});

const options = computed(() => ({
  center: userLocation.value.geometry.coordinates,
  fitBoundsOptions: { padding: 50 },
  zoom: 9,
  style: "/styles/radar.json",
  ...props.options,
}));

const layers = [
  ...SNOWFALL_SEASONAL_MAP_LEGEND.map((item, i) => {
    return {
      id: `snowfall-${i}`,
      source: "snow_depth",
      "source-layer": "snow_depth",
      type: "fill",
      paint: {
        "fill-color": item.color,
        "fill-opacity": {
          stops: [
            [0, 0.6],
            [12, i < 2 ? 0.5 : 0.4],
            [16, i < 2 ? 0.4 : 0.2],
          ],
        },
      },
      filter: ["==", "ID", i + 1],
      beforeId: "label_airport",
    };
  }),
  {
    id: "contour-text",
    type: "symbol",
    source: "snow_depth",
    "source-layer": "snow_depth",
    filter: [">", ["get", "min_inches"], 0],
    paint: {
      "text-color": "rgb(107, 115, 124)",
      "text-halo-color": "rgba(255, 255, 255, 0.75)",
      "text-halo-width": 1,
    },
    layout: {
      "symbol-placement": "line",
      "text-size": 10,
      "text-field": ["concat", ["number-format", ["get", "min_inches"], {}], " in"],
      "text-font": ["Noto Sans Bold"],
    },
  },
];

watch(
  () => props.location,
  () => {
    querySnowfall();
  }
);

function querySnowfall() {
  if (!map || !userLocation.value || !map.getBounds().contains(userLocation.value.geometry.coordinates)) {
    return;
  }

  // if the map is too zoomed out it will be inaccurate
  if (map.getZoom() < 5) {
    return;
  }

  const center = map.project(userLocation.value.geometry.coordinates);
  const features = map.queryRenderedFeatures(center).filter((feature) => feature.source === "snow_depth");
  if (!features.length) {
    snowfall.value = "0 in";
    return;
  }
  const minInches = features[0]?.properties?.min_inches;
  if (minInches < 1) {
    snowfall.value = "< 1 in";
  } else if (minInches < 12) {
    snowfall.value = new Intl.NumberFormat().format(minInches) + " in";
  } else {
    snowfall.value = new Intl.NumberFormat().format(minInches / 12) + " ft";
  }
}

function loadMap(mapValue) {
  map = mapValue;
  mapLoaded.value = true;
  map.on("data", () => {
    querySnowfall();
  });
  map.on("idle", () => {
    querySnowfall();
  });
}
</script>
